import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { observer } from '@legendapp/state/react';
import { ChecklistsList } from '../../components/checklist/ChecklistsList';
import { resetPagination } from '../../hooks/useChecklistsList';
import { AppAppIndexLogger } from '../../utils/logger';
import { globalState } from '../../state/globalState';
import UpgradePrompt from '@/components/subscription/UpgradePrompt';
import { hasPremiumAccess } from '@/utils/subscriptionAccess';
import { getUserProfile } from '../../services/userService';
import { isMotoristaUserType } from '@/utils/userRoles';

const logger = AppAppIndexLogger.Checklist;

/**
 * Tela de listagem de checklists com paginação
 * Implementação conforme item 3 do plano - Listagem paginada (📄 *NOVO*)
 * Agora usa o componente reutilizável ChecklistsList
 */
const ChecklistsScreen = observer(() => {
  // ✅ Obter usuário do estado global
  const user = globalState.user.user.get();
  const subscriptionSnapshot = globalState.subscription.get();
  const profileSnapshot = globalState.user.profile.get();
  const premiumAccess = hasPremiumAccess(subscriptionSnapshot, profileSnapshot);
  const motoristaScopeUserId = isMotoristaUserType(profileSnapshot?.user_type) ? user?.id || null : null;
  
  // Estados para gerenciar o carregamento do empresa_id
  const [empresaId, setEmpresaId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [initialized, setInitialized] = useState(false);

  const initializeScreen = useCallback(async () => {
    try {
      logger.info('Inicializando tela de checklists');
      setError(''); // Limpar erros anteriores
      
      // Reset da paginação ao montar a tela
      resetPagination();
      
      // ✅ Obter empresa_id real do perfil do usuário (mesmo padrão do index.tsx)
      if (!user?.id) {
        logger.warn('ID do usuário não disponível');
        setError('Usuário não autenticado');
        return;
      }

      logger.info('Obtendo perfil do usuário para extrair empresa_id');
      const { data: profile, error: profileError } = await getUserProfile(user.id);

      if (profileError) {
        logger.error('Erro ao obter perfil do usuário:', profileError);
        setError('Erro ao carregar perfil do usuário');
        return;
      }

      if (!profile?.empresa_id) {
        logger.warn('Usuário não possui empresa associada');
        setError('Usuário não possui empresa associada');
        return;
      }

      // ✅ Definir empresa_id real obtido do perfil
      setEmpresaId(profile.empresa_id);
      logger.info(`Empresa ID obtido do perfil: ${profile.empresa_id}`);
      
    } catch (error) {
      logger.error('Erro ao inicializar tela de checklists:', error);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setInitialized(true);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!premiumAccess) {
      logger.info('Plano free detectado – mantendo página de checklists bloqueada');
      resetPagination();
      setInitialized(true);
      return () => {
        resetPagination();
      };
    }

    initializeScreen();
    
    return () => {
      logger.info('Limpando estado da tela de checklists');
      resetPagination();
    };
  }, [premiumAccess, initializeScreen]);

  if (!premiumAccess) {
    return (
      <SafeAreaView style={[styles.container, styles.centered]}>
        <UpgradePrompt variant="restricted" />
      </SafeAreaView>
    );
  }

  // ✅ Loading inicial enquanto obtém empresa_id
  if (!initialized) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.loadingText}>Inicializando...</Text>
        </View>
      </SafeAreaView>
    );
  }

  // ✅ Se não tem empresa_id (erro ou usuário sem empresa), mostrar estado de erro
  if (!empresaId) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.emptyContainer}>
          <Text style={styles.errorText}>
            {error || 'Não foi possível carregar os dados'}
          </Text>
          <TouchableOpacity style={styles.retryButton} onPress={() => initializeScreen()}>
            <Text style={styles.retryText}>Tentar novamente</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* ✅ Usar componente reutilizável com configuração completa */}
      <ChecklistsList 
        empresaId={empresaId}
        usuarioId={motoristaScopeUserId}
        compact={false}
        showFilters={true}
        showHeader={true}
      />
    </SafeAreaView>
  );
});

export default ChecklistsScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5',
  },
  centered: {
    justifyContent: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    color: '#666666',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingVertical: 64,
  },
  errorText: {
    fontSize: 16,
    color: '#FF0000',
    textAlign: 'center',
    marginBottom: 16,
  },
  retryButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#007AFF',
  },
  retryText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
}); 
